import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Store, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useSeo } from "@/hooks/useSeo";

function fmtErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function OwnerAuth() {
  useSeo("Business Owner Login | OyeBest", "Login or register to manage your business on OyeBest.");
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [li, setLi] = useState({ email: "", password: "" });
  const [rg, setRg] = useState({ name: "", email: "", password: "" });

  if (!loading && (user?.role === "owner" || user?.role === "admin")) return <Navigate to="/owner/dashboard" replace />;

  const doLogin = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try { await login(li.email, li.password); navigate("/owner/dashboard"); }
    catch (e) { setError(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/register", rg);
      localStorage.setItem("oyebest_token", data.token);
      window.location.href = "/owner/dashboard";
    } catch (e) { setError(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0f1115] px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <Link to="/" className="font-display text-3xl font-extrabold text-white">Oye<span className="text-primary">Best</span></Link>
          <p className="text-white/50 mt-1 text-sm flex items-center justify-center gap-1.5"><Store className="w-4 h-4" /> Business Owner Portal</p>
        </div>
        <div className="bg-white rounded-3xl p-6 shadow-2xl">
          <Tabs defaultValue="login">
            <TabsList className="grid grid-cols-2 w-full rounded-full mb-5">
              <TabsTrigger value="login" className="rounded-full" data-testid="tab-login">Login</TabsTrigger>
              <TabsTrigger value="register" className="rounded-full" data-testid="tab-register">Register</TabsTrigger>
            </TabsList>
            {error && <p className="text-sm text-destructive mb-3" data-testid="owner-auth-error">{error}</p>}
            <TabsContent value="login">
              <form onSubmit={doLogin} className="space-y-4">
                <div className="space-y-1.5"><Label className="text-sm font-semibold">Email</Label><Input type="email" value={li.email} onChange={(e) => setLi({ ...li, email: e.target.value })} className="rounded-xl h-12" data-testid="owner-login-email" /></div>
                <div className="space-y-1.5"><Label className="text-sm font-semibold">Password</Label><Input type="password" value={li.password} onChange={(e) => setLi({ ...li, password: e.target.value })} className="rounded-xl h-12" data-testid="owner-login-password" /></div>
                <Button type="submit" disabled={busy} className="w-full rounded-full h-12 font-semibold gap-2" data-testid="owner-login-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Login</Button>
              </form>
            </TabsContent>
            <TabsContent value="register">
              <form onSubmit={doRegister} className="space-y-4">
                <div className="space-y-1.5"><Label className="text-sm font-semibold">Your Name</Label><Input value={rg.name} onChange={(e) => setRg({ ...rg, name: e.target.value })} className="rounded-xl h-12" data-testid="owner-reg-name" /></div>
                <div className="space-y-1.5"><Label className="text-sm font-semibold">Email</Label><Input type="email" value={rg.email} onChange={(e) => setRg({ ...rg, email: e.target.value })} className="rounded-xl h-12" data-testid="owner-reg-email" /></div>
                <div className="space-y-1.5"><Label className="text-sm font-semibold">Password</Label><Input type="password" value={rg.password} onChange={(e) => setRg({ ...rg, password: e.target.value })} className="rounded-xl h-12" data-testid="owner-reg-password" /></div>
                <Button type="submit" disabled={busy} className="w-full rounded-full h-12 font-semibold gap-2" data-testid="owner-register-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Create Account</Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
        <p className="text-center text-white/40 text-xs mt-5">List your business for free and start getting customers.</p>
      </div>
    </div>
  );
}
